/**
 * vacationWeekSplits.ts
 * Pure helper for splitting an absence date range into per-week rows.
 *
 * Follow-up 1.6 — Per-week working-days call (clamped spans + holiday chips).
 * Wave 3 (percent retirement, Q2A): ALL absence types are WHOLE-DAY on their
 * dated span, so every split row writes Percent = 100 (the old
 * workingDays × 20 partial percent is retired; readers key on AbsenceType +
 * StartDate/EndDate, not the stored number). The per-week working-day counts
 * and holidays are kept — they drive the dialog's per-week chips.
 *
 * Usage:
 *   1. Caller fetches GET /api/schedule/working-days for each week individually.
 *   2. Results are collected into a Map<weekStart, workingDayCount>.
 *   3. computeVacationWeekSplits(startDate, endDate, map) returns the split rows.
 */

// ---------------------------------------------------------------------------
// Type
// ---------------------------------------------------------------------------

export interface VacationWeekSplit {
  /** BST week-start Saturday 'YYYY-MM-DD'. */
  weekStart: string;
  /** BST week-end Friday 'YYYY-MM-DD'. */
  weekEnd: string;
  /** Effective vacation start within this week (clamped to vacation range). */
  effectiveStart: string;
  /** Effective vacation end within this week (clamped to vacation range). */
  effectiveEnd: string;
  /** Working-day count for [effectiveStart, effectiveEnd] in this week. */
  workingDays: number;
  /** Always 100 — whole-day absence (Q2A). Kept for the wire payload shape. */
  percent: number;
  /** Stat holidays that fall in this week's effective range. */
  holidays: string[];
}

// ---------------------------------------------------------------------------
// Helpers (pure, no imports, self-contained)
// ---------------------------------------------------------------------------

function addDays(iso: string, n: number): string {
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d + n));
  return dt.toISOString().slice(0, 10);
}

function clamp(date: string, lo: string, hi: string): string {
  if (date < lo) return lo;
  if (date > hi) return hi;
  return date;
}

/** Return the BST week-start Saturday for a given date. */
function weekStartFor(dateStr: string): string {
  const [y, m, d] = dateStr.split('-').map(Number);
  const dow = new Date(Date.UTC(y, m - 1, d)).getUTCDay(); // 0=Sun,6=Sat
  const diff = dow === 6 ? 0 : -(dow + 1);
  return addDays(dateStr, diff);
}

/** Return all BST week-start Saturdays that overlap [startDate, endDate]. */
function weekStartsInRange(startDate: string, endDate: string): string[] {
  const starts: string[] = [];
  let cur = weekStartFor(startDate);
  while (cur <= endDate) {
    starts.push(cur);
    cur = addDays(cur, 7);
  }
  return starts;
}

// ---------------------------------------------------------------------------
// Main export
// ---------------------------------------------------------------------------

/**
 * Compute per-week vacation allocation rows for a date range.
 *
 * @param startDate         Vacation start date 'YYYY-MM-DD'.
 * @param endDate           Vacation end date 'YYYY-MM-DD'.
 * @param workingDaysByWeek Map of weekStart → { workingDays, holidays }.
 *                          Must cover all weeks in [startDate, endDate].
 *                          If a week is absent, falls back to 5 days (100%).
 * @returns                 One entry per BST week overlapping the range.
 */
export function computeVacationWeekSplits(
  startDate: string,
  endDate: string,
  workingDaysByWeek: ReadonlyMap<string, { workingDays: number; holidays: string[] }>,
): VacationWeekSplit[] {
  if (!startDate || !endDate || startDate > endDate) return [];

  const weekStarts = weekStartsInRange(startDate, endDate);

  return weekStarts.map((ws) => {
    const wFri = addDays(ws, 6);
    const effectiveStart = clamp(startDate, ws, wFri);
    const effectiveEnd = clamp(endDate, ws, wFri);

    const entry = workingDaysByWeek.get(ws);
    const workingDays = entry?.workingDays ?? 5; // fallback: assume full week
    const holidays = entry?.holidays ?? [];
    const percent = 100; // whole-day absence (Q2A) — server pins this anyway

    return {
      weekStart: ws,
      weekEnd: wFri,
      effectiveStart,
      effectiveEnd,
      workingDays,
      percent,
      holidays,
    };
  });
}
